import { z } from 'zod'
import { WorkflowConditionConfigSchema } from '@omnea-app/packages/universal/conditionality/schema'
import { UserConditionConfigSchema } from '@omnea-app/packages/universal/conditionality/userCondition'
import { ref } from '@omnea-app/packages/universal/references/refKinds'
import type { PrimitiveType } from '@omnea-digital/data-variables'

// Every type here infers from its schema: a hand-written type beside one lets a field reach the
// compiler and not the walk, and a field the schema omits is walked in silence.

const TRANSFORMATION_OUTPUT_PRIMITIVES = ['number', 'text', 'boolean', 'entity'] as const satisfies readonly PrimitiveType[]

const TransformationOutputPrimitiveSchema = z.enum(TRANSFORMATION_OUTPUT_PRIMITIVES)
export type TransformationOutputPrimitive = z.infer<typeof TransformationOutputPrimitiveSchema>

const FormFieldReferenceSchema = z.object({ sourceType: z.literal('form-field'), sourceId: z.string() })

export const VariableReferenceSchema = z.discriminatedUnion('sourceType', [
  FormFieldReferenceSchema,
  z.object({ sourceType: z.literal('request-context'), sourceId: z.string() }),
  // Values computed at execution time that depend on neither the request nor a previous
  // block (e.g. Today). `sourceId` is a ReferenceValueId — see referenceValues.ts.
  z.object({ sourceType: z.literal('reference-value'), sourceId: z.string() }),
  z.object({
    sourceType: z.literal('block-output'),
    sourceId: z.string(),
    blockId: z.string(),
    blockName: z.string(),
  }),
  // Used only by External Operation custom primary output. `sourceId` is the output
  // mapping key (= form question name); extops resolves it via `outputMapping[sourceId]`.
  z.object({ sourceType: z.literal('output-field'), sourceId: z.string() }),
])
export type VariableReference = z.infer<typeof VariableReferenceSchema>

export const VariableArgSchema = z.object({ kind: z.literal('variable'), source: VariableReferenceSchema })

const TransformationArgSchema = z.discriminatedUnion('kind', [
  VariableArgSchema,
  z.object({ kind: z.literal('static'), value: z.string() }),
])
export type TransformationArg = z.infer<typeof TransformationArgSchema>

const OutputEntitySourceSchema = ref('coreDataSet').optional()

// An entity output's static values are options of the `outputEntitySource` set; every other output's are plain text.
// Split on `outputPrimitive`, as the runtime does — a non-entity output can still carry `outputEntitySource`.
const EntityOutputArgSchema = z.discriminatedUnion('kind', [
  VariableArgSchema,
  z.object({ kind: z.literal('static'), value: ref('coreDataOption', { scopeFrom: 'outputEntitySource' }) }),
])

// Absent is text: the editor writes no output type until one is picked.
const NonEntityOutputPrimitiveSchema = TransformationOutputPrimitiveSchema.exclude(['entity']).optional()

const TransformationRuleSchema = z.object({
  id: z.string(),
  when: TransformationArgSchema,
  then: TransformationArgSchema,
})
export type TransformationRule = z.infer<typeof TransformationRuleSchema>

const SwitchBranchSchema = z.object({
  id: z.string(),
  when: WorkflowConditionConfigSchema,
  then: TransformationArgSchema,
})
export type SwitchBranch = z.infer<typeof SwitchBranchSchema>

const NonEntityRulesTransformationSchema = z.object({
  kind: z.literal('rules'),
  subject: TransformationArgSchema,
  rules: z.array(TransformationRuleSchema),
  defaultValue: TransformationArgSchema.optional(),
  outputPrimitive: NonEntityOutputPrimitiveSchema,
  outputEntitySource: OutputEntitySourceSchema,
})

const EntityRulesTransformationSchema = z.object({
  ...NonEntityRulesTransformationSchema.shape,
  rules: z.array(z.object({ ...TransformationRuleSchema.shape, then: EntityOutputArgSchema })),
  defaultValue: EntityOutputArgSchema.optional(),
  outputPrimitive: z.literal('entity'),
})

const NonEntitySwitchTransformationSchema = z.object({
  kind: z.literal('switch'),
  branches: z.array(SwitchBranchSchema),
  defaultValue: TransformationArgSchema.optional(),
  outputPrimitive: NonEntityOutputPrimitiveSchema,
  outputEntitySource: OutputEntitySourceSchema,
  // Emit every matching branch's `then` as a list (multi-select dropdowns) instead of only the first match.
  collectAll: z.boolean().optional(),
})

const EntitySwitchTransformationSchema = z.object({
  ...NonEntitySwitchTransformationSchema.shape,
  branches: z.array(z.object({ ...SwitchBranchSchema.shape, then: EntityOutputArgSchema })),
  defaultValue: EntityOutputArgSchema.optional(),
  outputPrimitive: z.literal('entity'),
})

/** A condition evaluated against something other than the request; `subject` names what against. */
const SubjectPredicateSchema = z.object({ subject: z.literal('user'), condition: UserConditionConfigSchema })
export type SubjectPredicate = z.infer<typeof SubjectPredicateSchema>

// Among the request-context fields only the requestor resolves to a user id, so the rest are excluded
// here rather than left to fail at runtime.
export const ManagerWalkStartSchema = z.union([
  FormFieldReferenceSchema,
  z.object({ sourceType: z.literal('request-context'), sourceId: z.literal('request.requestor') }),
])
export type ManagerWalkStart = z.infer<typeof ManagerWalkStartSchema>

// Primitive and shape are never stored: the editor derives them from the source variable and the
// runtime from the resolved value, so nothing here is the place to look for them.
export const ReferenceTransformationSchema = z.object({ kind: z.literal('reference'), source: VariableReferenceSchema })

const TransformationValueSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('function'), functionName: z.string(), args: z.array(TransformationArgSchema) }),
  z.discriminatedUnion('outputPrimitive', [EntityRulesTransformationSchema, NonEntityRulesTransformationSchema]),
  ReferenceTransformationSchema,
  z.discriminatedUnion('outputPrimitive', [EntitySwitchTransformationSchema, NonEntitySwitchTransformationSchema]),
  z.object({
    // Copies/merges file attachments from one-or-many source variables into a
    // single file/list output. Each source is resolved to its native
    // VariableValue; only file-primitive results are kept, their files
    // concatenated in source order and de-duplicated by fileStorageId.
    // Non-file or unresolvable sources are skipped (suppliers may leave
    // upload slots empty), never thrown on.
    kind: z.literal('copy-files'),
    sources: z.array(VariableReferenceSchema),
  }),
  z.object({
    // Emits the nearest manager above the start user satisfying `predicate`; a no-match skips the output.
    kind: z.literal('manager-walk'),
    start: ManagerWalkStartSchema,
    predicate: SubjectPredicateSchema,
  }),
])
export type TransformationValue = z.infer<typeof TransformationValueSchema>

export const TransformationOutputFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  outputId: z.string(),
  transformation: TransformationValueSchema,
})
export type TransformationOutputField = z.infer<typeof TransformationOutputFieldSchema>
